import { enumFilterModes, enumLockStatus } from "../constants";
import { ExcludeFromCompress } from "../utils/ExcludeFromCompress";
import { locationObj } from "../utils/locationObj";
import { QFilterMgr } from "./QFilterMgr";
import { QHighlightMgr } from "./QHighlightMgr";
import { QObjDBObject } from "./QObjDBObject";
import { QObjRefLib } from "./QObjRefLib";
import { QXMLObject } from "./QXMLObject";
import { SysGen } from "../utils/SysGen";
import { binary_objsearch_iterative } from "../utils/binary_objsearch_iterative";

export class QObjMgr extends QXMLObject {
  constructor() {
    super();
    this.fClass = null;
    this.fObjectList = null;
    this.fNonPersistList = null;
    this.fFilteredObjList = null;

    this.fRefLib = null;
    /**
     * @type {(null | QFilterMgr)}
     */
    this.fFilterMgr = null;
    /**
     * @private
     * @type {(null | QHighlightMgr)}
     */
    this.fHighlightMgr = null;
    this.fMgrActionsMgr = null;
  }

  ApplyDeleteFiltering(revert, targetObj, targetHolders = false) {
    var deleteTarget = !revert;
    if (deleteTarget) {
      this.FilterObject(targetObj, true);
    } else {
      this.RestoreObject(targetObj);
    }
  }

  InitObjMgr(theClass) {
    this.fClass = theClass;
    this.fObjectList = [];
    this.fNonPersistList = [];
    this.fFilteredObjList = [];

    this.fFilterMgr = new QFilterMgr();
    this.fFilterMgr.InitFilterMgr(this.fClass);

    this.fHighlightMgr = new QHighlightMgr();
    this.fHighlightMgr.InitHighlightMgr(this.fClass);

    this.SetXMLElementName("qobjectmgr");
  }

  TagFilteredObjs() {
    //TODO: needs to tag currently filtered objects and new objects.
    const docRef = this.fClass.GetDocRef();
    const filterMgr = this.fFilterMgr;

    const filteredObjList = [];
    const n = this.GetObjectCount();

    const isMuted = filterMgr.GetFilterMode() === enumFilterModes.mute;
    const ctx = filterMgr.PrepareFilterContext();

    if (ctx.hasSavedFilters || ctx.hasActiveFilter) {
      for (var i = 0; i < n; i++) {
        const dbObj = this.GetIndexObject(i);
        dbObj.SetMuted(false);

        const savedFilterMatch = filterMgr.EvalSavedFiltersCtx(dbObj, docRef, ctx);
        const interactiveFilterMatch = filterMgr.EvalInteractiveFilterCtx(dbObj, docRef, ctx);
        let shouldFilter = savedFilterMatch || interactiveFilterMatch;

        if (shouldFilter && dbObj.IsPositionObj()) {
          const holdersList = dbObj.GetHolderList();

          if (holdersList.length === 0) {
            filteredObjList.push(dbObj);
            continue;
          }

          const hasMatchingHolder = holdersList.some(holder => {
            return !holder.IsFiltered();
          });

          if (!hasMatchingHolder) {
            shouldFilter = false;
          }
        }

        if (!shouldFilter) {
          continue;
        }

        if (isMuted && !dbObj.IsDeleted()) {
          dbObj.SetMuted(true);
        }

        filteredObjList.push(dbObj);
      }
    }

    return filteredObjList;
  }

  TagPositionFilteredObjs() {
    //TODO: needs to tag currently filtered objects and new objects.
    const docRef = this.fClass.GetDocRef();
    const filterMgr = docRef.GetFilterMgr();

    const filteredObjMap = {};
    const n = this.GetObjectCount();

    const isMuted = filterMgr.GetFilterMode() === enumFilterModes.mute;
    const ctx = filterMgr.PrepareFilterContext();

    if (!ctx.hasSavedFilters && !ctx.hasActiveFilter) {
      return [];
    }

    const addToFilterMap = dbObj => {
      filteredObjMap[dbObj.GetObjectID()] = dbObj;
      if (isMuted && !dbObj.IsDeleted()) {
        dbObj.SetMuted(true);
      }
    };
    // Slightly different from TagFilteredObjs, this one evaluates positions and holders at the same time.
    this.fObjectList.forEach(dbObj => {
      dbObj.SetMuted(false);
      const holdersList = dbObj.GetHolderList();
      // Unmute all holders initially
      holdersList.forEach(h => h.SetMuted(false));

      // Apply saved filters first
      if (ctx.hasSavedFilters) {
        // Only apply saved filters to holders since position fields' support for saved filters isn't added yet.
        holdersList.forEach(holder => {
          const holderMatchesSavedFilter = filterMgr.EvalSavedFiltersCtx(holder, docRef, ctx);
          if (holderMatchesSavedFilter) {
            addToFilterMap(holder);
          }
        });
      }

      // Apply interactive filters
      // Interactive filters behave differently than saved filters.
      if (ctx.hasActiveFilter) {
        const result = filterMgr.EvalPositionsInteractiveFilterCtx(dbObj, docRef, ctx);
        const [positionPassesFilter, holdersCondition] = result.getConditions();

        holdersList.forEach(holder => {
          const holderPassesFilter = holdersCondition[holder.GetObjectID()];
          if (holderPassesFilter && positionPassesFilter) return;
          addToFilterMap(holder);
        });

        if (!positionPassesFilter) {
          addToFilterMap(dbObj);
        }
      }
    });

    return Object.values(filteredObjMap);
  }

  RemoveFilteredObjects(filteredObjs) {
    //Step 1: Restore previously filtered objects that should be returned.
    var didFilter = false;
    var PrevFilteredObjList = [];
    const filteredObjSet = new Set(filteredObjs);
    var n = this.GetFilteredObjectCount();
    for (var i = 0; i < n; i++) {
      var theObject = this.GetIndexFilteredObject(i);
      if (!filteredObjSet.has(theObject)) PrevFilteredObjList.push(theObject);
    }

    for (var i = 0; i < PrevFilteredObjList.length; i++) {
      var priorFilteredObj = PrevFilteredObjList[i];
      // prevent deletes from coming back as a result of filter  reset
      if (priorFilteredObj.fIsDeleted === true) continue;
      this.RestoreObject(priorFilteredObj);
      if (didFilter == false) didFilter = true;
    }

    //Step 2: Handle newly filtered objects
    for (var i = 0; i < filteredObjs.length; i++) {
      var theObject = filteredObjs[i];
      this.FilterObject(theObject);
      if (didFilter == false) didFilter = true;
    }

    const filterMgr = this.fFilterMgr;
    if (filterMgr.GetFilterMode() === enumFilterModes.includefilteredmgrs) {
      this.fFilteredObjList.forEach(dbObj => {
        if (dbObj.IsFiltered() && dbObj.IncludedFilteredManager()) {
          dbObj.SetMuted(true);
        }
      });
    }
    return didFilter;
  }

  GetFilterMgr() {
    return this.fFilterMgr;
  }

  GetHighlightMgr() {
    return this.fHighlightMgr;
  }

  GetMgrActionMgr() {
    return this.fMgrActionsMgr;
  }

  GetPersistObjectCount() {
    var np = this.fNonPersistList.length;
    var no = this.fObjectList.length;
    return no - np;
  }

  GetObjectCount() {
    return this.fObjectList.length;
  }

  GetObjectList() {
    return this.fObjectList;
  }

  GetObjectListNonPersist() {
    return this.fNonPersistList;
  }

  SetObjectList(objList, nonPersist) {
    this.fClass.JournalObjList();
    this.fObjectList = objList;
    this.fNonPersistList = nonPersist;
  }

  GetIndexObject(index) {
    return this.fObjectList[index];
  }

  AddFilter(aFilter) {
    this.fFilterMgr.AddFilter(aFilter);
  }

  GetFilteredObjectCount() {
    return this.fFilteredObjList.length;
  }

  GetIndexFilteredObject(index) {
    return this.fFilteredObjList[index];
  }

  GetFilteredObjectIndex(obj) {
    return this.fFilteredObjList.indexOf(obj);
  }

  GetFilteredObjList() {
    return this.fFilteredObjList;
  }

  ResetFilterList() {
    this.fFilteredObjList = [];
  }

  FilterObject(theObj, tagDeleted) {
    theObj.Journal(false);
    theObj.fFiltered = true;
    if (tagDeleted) {
      theObj.fIsDeleted = true;
    }

    var filterIndex = this.GetFilteredObjectIndex(theObj);
    if (filterIndex == -1) {
      this.fFilteredObjList.push(theObj);
      return true;
    }
    return false;
  }

  RestoreObject(theObj) {
    theObj.Journal(false);
    var index = this.GetFilteredObjectIndex(theObj);
    if (index > -1) {
      this.fFilteredObjList.splice(index, 1);
      theObj.fFiltered = false;
      theObj.fIsDeleted = false;
      theObj.SetMuted(false);
    }
  }

  AddHighlight(aHighlight) {
    this.fHighlightMgr.AddHighlight(aHighlight);
  }

  SyncHighlights(highlightList) {
    this.fHighlightMgr.SyncHighlights(highlightList);
  }

  InsertObject(newObject) {
    if (this.fObjectList.length == 0) this.fObjectList.push(newObject);
    else {
      if (this.fObjectList == null) console.log("Bad bad");

      var l = locationObj(newObject.GetObjectID(), this.fObjectList);
      this.fObjectList.splice(l + 1, 0, newObject);
    }
  }

  BulkInsertObject(newObject) {
    this.fObjectList.push(newObject);
  }

  FinalizeBulkInsert() {
    this.fObjectList.sort(function (a, b) {
      var idA = a.GetObjectID();
      var idB = b.GetObjectID();
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    });
  }

  AddNewObject() {
    var newObject = new QObjDBObject();
    newObject.InitNewObject(this.fClass);
    this.InsertObject(newObject);

    return newObject;
  }

  ResetFieldIndex() {
    var n = this.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var o = this.fObjectList[i];
      o.ResetFieldIndex();
    }
  }

  GetRootObjs() {
    var n = this.GetObjectCount();
    var roots = [];

    for (var i = 0; i < n; i++) {
      var o = this.fObjectList[i];
      if (!o.IsDeleted() && o.IsPersistent() && o.IsRoot()) roots.push(o);
    }

    return roots;
  }

  AnyObjectLocked() {
    var n = this.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var o = this.fObjectList[i];
      if (o.IsPersistent() && o.GetLockStatus() != enumLockStatus.notLocked) return true;
    }

    return false;
  }

  RetrieveObject(objectID) {
    var o = null;
    var p = binary_objsearch_iterative(this.fObjectList, objectID);
    if (p != null) o = this.fObjectList[p];

    return o;
  }

  // Build a Map<fieldValue, object> for O(1) lookups. Fresh map each call, no caching.
  // Parallel to QSubChartMgr.GetSCNodeMapByDataObjID().
  BuildObjectMap(fieldDef, persistOnly) {
    var map = new Map();
    var n = this.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var o = this.fObjectList[i];
      if (persistOnly && !o.IsPersistent()) continue;
      var ov = o.GetFieldValue(fieldDef);
      if (ov != null) map.set(ov, o);
    }
    return map;
  }

  FindObject(fieldDef, fieldValue, persistOnly) {
    var n = this.GetObjectCount();
    var checkIt = true;
    var o;

    for (var i = 0; i < n; i++) {
      o = this.fObjectList[i];
      if (persistOnly) checkIt = o.IsPersistent();
      else checkIt = true;

      if (checkIt) {
        var ov = o.GetFieldValue(fieldDef);
        if (ov == fieldValue) return o;
      }
    }

    return null;
  }

  FindMatchingObjects(fieldDef, fieldValue, persistOnly) {
    var n = this.GetObjectCount();
    var checkIt = true;
    var o;
    var objList = [];
    for (var i = 0; i < n; i++) {
      o = this.fObjectList[i];
      if (persistOnly) checkIt = o.IsPersistent();
      else checkIt = true;

      if (checkIt) {
        var ov = o.GetFieldValue(fieldDef);
        if (ov == fieldValue) objList.push(o);
      }
    }
    return objList;
  }

  FindMatchingObjectsByPredicate(predicate) {
    const objList = this.fObjectList;
    return objList.filter(predicate);
  }

  FindObjectPartial(fieldDef, fieldValue, persistOnly) {
    var n = this.GetObjectCount();
    var checkIt = true;
    var o;

    for (var i = 0; i < n; i++) {
      o = this.fObjectList[i];
      /*
		if (persistOnly)
			checkIt = o.IsPersistent();
		else
			checkIt = true;
		*/
      checkIt = o["objfilter"] !== "exclude";
      if (checkIt) {
        var ov = o.GetFieldValue(fieldDef);
        if (ov.indexOf(fieldValue) == 0) return o;
      }
    }

    return null;
  }

  UpdateNonPersistList(dbObj, persist) {
    var i = this.fNonPersistList.indexOf(dbObj);
    if (persist) {
      if (i != -1) this.fNonPersistList.splice(i, 1);
    } else if (i == -1) this.fNonPersistList.push(dbObj);
  }

  DeleteObject(theObj) {
    var index = this.fObjectList.indexOf(theObj);
    if (index != -1) {
      this.UpdateNonPersistList(theObj, true);
      this.fObjectList.splice(index, 1);
      return true;
    }

    return false;
  }

  LoadFromJSON(json, employeeMap) {
    var parentClass = this.fClass;
    var objDB = parentClass.GetObjDB();
    var version = objDB.GetVersion();
    const doc = this.fClass.GetDocRef();

    var libInfo = json["refLib"];
    if (libInfo) {
      var refLib = new QObjRefLib();
      refLib.InitRefLib(parentClass);
      this.fRefLib = refLib;
      refLib.LoadFromJSON(libInfo);
    }

    var objects = json["objects"];
    for (var i = 0; i < objects.length; i++) {
      var objData = objects[i];
      var loadObj = new QObjDBObject();
      loadObj.InitObject(parentClass);
      loadObj.LoadFromJSON(objData, employeeMap);
      this.fObjectList.push(loadObj);
    }

    var filterMgr = json["filterMgr"];

    // Position class shares Box's filter manager — never load a separate one for it.
    if (filterMgr && this.fClass?.GetClassName() !== "Position") {
      var loadObj = new QFilterMgr();
      loadObj.InitFilterMgr(parentClass);
      loadObj.LoadFromJSON(filterMgr);
      if (this.fClass?.GetDocRef()?.IsCustomChart()) {
        loadObj.LoadLocalFilters();
      }
      this.fFilterMgr = loadObj;
    }

    var highlightMgr = json["highlightMgr"];
    if (highlightMgr) {
      var loadObj = new QHighlightMgr();
      loadObj.InitHighlightMgr(parentClass);
      loadObj.LoadFromJSON(highlightMgr);
      this.fHighlightMgr = loadObj;
    }

    // Always restore the user's interactive highlights for custom charts,
    // regardless of whether the chart JSON contains chart-level highlights.
    if (doc?.IsCustomChart()) {
      var cachedHighlightInfo = this.fHighlightMgr.GetFullGroupList();
      var myHighlightInfo = cachedHighlightInfo.filter(
        highlight =>
          highlight.GetGroupIdentifier() === this.fHighlightMgr.GetFilterGroupIdentifier(doc)
      );
      this.fHighlightMgr.SetInteractiveHighlights(myHighlightInfo);
    }

    var n = parentClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = parentClass.GetIndexFieldDef(i);
      fieldDef["lookup"] = null;
    }

    this.fRefLib = null;
  }

  OutputToJSON(stream) {
    var objObj = {};
    var theClassName = this.fClass.GetClassName();
    objObj["class"] = theClassName;

    //This is the replacement for compressObjs
    var theC = this.fClass;
    var refLib = new QObjRefLib();
    refLib.InitRefLib(theC);
    this.fRefLib = refLib;

    var n = this.GetObjectCount();
    var includeCount = 0;
    for (var i = 0; i < n; i++) {
      var anObject = this.GetIndexObject(i);
      if (anObject.IsPersistent()) {
        if (anObject["objfilter"] != "exclude" || anObject.fIsDeleted) {
          anObject.CompressObj(refLib);
          if (anObject.fCloneBase) {
            anObject.fCloneBase.CompressObj(refLib);
          }
          includeCount++;
        }
      }
    }

    this.CullRefTable(includeCount);
    objObj["refLib"] = refLib.OutputToJSON(stream);

    //Output standard objects
    var n = this.GetObjectCount();
    var objects = [];
    for (var i = 0; i < n; i++) {
      var anObject = this.GetIndexObject(i);
      if (anObject.IsPersistent()) {
        if (anObject["objfilter"] != "exclude" || anObject.fIsDeleted)
          objects.push(anObject.OutputToJSON(null));
      }
    }
    objObj["objects"] = objects;
    this.fRefLib = null;
    objObj["highlightMgr"] = this.fHighlightMgr.OutputToJSON(stream);
    objObj["filterMgr"] = this.fFilterMgr.OutputToJSON(stream);

    return objObj;
  }

  OutputSubElements(stream) {
    var theClassName = this.fClass.GetClassName();
    this.OutputSimpleElementF("classname", theClassName, stream);
  }

  SetObjFilter(value, tagOrphans) {
    var n = this.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var anObject = this.GetIndexObject(i);
      anObject["objfilter"] = value;
      anObject["ignoreSCTop"] = false;
      if (tagOrphans && anObject.IsOrphan()) anObject["objfilter"] = "orphan";
    }
  }

  CullRefTable(includeCount) {
    var theC = this.fClass;
    var n = theC.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = theC.GetIndexFieldDef(i);
      var fieldID = fieldDef.GetFieldID();
      var isCalc = fieldDef.GetCalcCode() != null;
      if (!isCalc) {
        var sysGen = SysGen(fieldID) || ExcludeFromCompress(fieldID);
        if (!sysGen) this.fRefLib.CullRefTable(fieldDef, includeCount);
      }
    }
  }

  CompressObjs(stream) {
    var theC = this.fClass;
    var refLib = new QObjRefLib();
    refLib.InitRefLib(theC);
    this.fRefLib = refLib;

    var n = this.GetObjectCount();
    var includeCount = 0;
    for (var i = 0; i < n; i++) {
      var anObject = this.GetIndexObject(i);
      if (anObject.IsPersistent()) {
        if (anObject["objfilter"] != "exclude") {
          anObject.CompressObj(refLib);
          includeCount++;
        }
      }
    }

    this.CullRefTable(includeCount);
    refLib.OutputToXML(stream);
  }

  GetRefLib() {
    return this.fRefLib;
  }

  OutputChildElements(stream) {
    this.CompressObjs(stream);

    //Output standard objects
    var n = this.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var anObject = this.GetIndexObject(i);
      if (anObject.IsPersistent()) {
        if (anObject["objfilter"] != "exclude") anObject.OutputToXML(stream);
      }
    }

    //Output Filtered Objects
    /*
	n = this.GetFilteredObjectCount();
	for (i=0;i<n;i++)
	{
		var anObject = this.GetIndexFilteredObject(i);
		if (anObject.IsFiltered())
			anObject.OutputToXML(stream);
		else
			console.log('this should not happen');
	}
	*/

    this.fRefLib = null;
    this.fHighlightMgr.OutputToXML(stream);
    this.fFilterMgr.OutputToXML(stream);
  }

  GetXMLElementName() {
    return this.fXMLElementName;
  }

  LoadSubElements(objTree) {
    //this.fFilterMgr.LoadFromXML(objTree);
  }

  LoadChildElements(objTree) {
    var parentClass = this.fClass;
    var objDB = parentClass.GetObjDB();
    var version = objDB.GetVersion();

    var libInfo = objTree.GetChildByName("qreflib");
    if (libInfo) {
      var refLib = new QObjRefLib();
      refLib.InitRefLib(parentClass);
      this.fRefLib = refLib;
      refLib.LoadFromXML(libInfo);
    }

    var s1 = "qo";
    if (version == 1) s1 = "qobject";

    var childList = objTree.GetChildOfTypeList(s1); // "qobject"
    var n = childList.length;
    for (var i = 0; i < n; i++) {
      var objData = childList[i];
      var loadObj = new QObjDBObject();
      loadObj.InitObject(parentClass);
      loadObj.LoadFromXML(objData);
      this.fObjectList.push(loadObj);
    }

    var childList = objTree.GetChildOfTypeList("filtermgr"); // Loading filter info
    if (childList[0]) {
      var loadObj = new QFilterMgr();
      loadObj.InitFilterMgr(parentClass);
      loadObj.LoadFromXML(childList[0]);
      this.fFilterMgr = loadObj;
    }

    var childList = objTree.GetChildOfTypeList("highlightmgr"); // Loading highlight info
    if (childList[0]) {
      var loadObj = new QHighlightMgr();
      loadObj.InitHighlightMgr(parentClass);
      loadObj.LoadFromXML(childList[0]);
      this.fHighlightMgr = loadObj;
    }

    n = parentClass.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = parentClass.GetIndexFieldDef(i);
      fieldDef["lookup"] = null;
    }

    this.fRefLib = null;
  }

  WireHierarchy() {
    var n = this.GetObjectCount();
    for (var i = 0; i < n; i++) {
      var o = this.fObjectList[i];
      var tempParentID = o.GetTempParentID();
      var tempChildIndex = o.GetTempChildIndex();

      var parent = null;
      if (tempParentID != -1) parent = this.RetrieveObject(tempParentID);

      o.SetParent(parent, -1);
      o.SetSortKey(tempChildIndex);
      delete o.fTempParentID;
      delete o.fTempChildIndex;
    }

    for (var anObjIdx in this.fObjectList) {
      var anObj = this.fObjectList[anObjIdx];
      anObj.DoSortChildren();
      if (anObj.IsFiltered()) this.fFilteredObjList.push(anObj);
    }
    /*
	for (var anObjIdx in this.fFilteredObjList)
	{
		var anObj = this.fFilteredObjList[anObjIdx];
		var index = this.fObjectList.indexOf(anObj);
		if (index>-1)
			this.fObjectList.splice(index,1);

	}
	 */
  }

  PurgeData() {
    this.fObjectList = [];
    this.fNonPersistList = [];
    this.fFilteredObjList = [];
  }

  GetUniqueValuesForField(fieldID) {
    const values = [];
    const objects = this.GetObjectList();
    for (const dbObj of objects) {
      const value = dbObj.SmartGetFieldValue(fieldID);
      values.push(`${value}`);
    }

    const uniqueValues = Array.from(new Set(values));
    uniqueValues.sort((a, b) => a.localeCompare(b));

    return uniqueValues;
  }
}
